import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

/** Банк вопросов (SP4 §4): сводка по банкам — для S4 «спрашивали N раз». */
export type QuestionBank = components["schemas"]["QuestionBank"];
export type Question = components["schemas"]["Question"];

export function useQuestionBanks() {
  return useQuery({
    queryKey: ["questions", "banks"],
    queryFn: async () => {
      const { data } = await api.GET("/questions/banks");
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Вопросы банка по порядку; ответы приходят только staff. */
export function useQuestions(bank: string | undefined) {
  return useQuery({
    queryKey: ["questions", "bank", bank],
    enabled: !!bank,
    queryFn: async () => {
      const { data } = await api.GET("/questions/banks/{bank}", { params: { path: { bank: bank! } } });
      return data ?? [];
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Подпись частоты: «12 вопросов · спрашивали 37 раз»; пусто, если банка ещё нет. */
export function bankStats(banks: QuestionBank[] | undefined, name: string, plural: (n: number, one: string, few: string, many: string) => string): string {
  const b = banks?.find((x) => x.name === name);
  if (!b) return "";
  const items = `${b.items} ${plural(b.items, "вопрос", "вопроса", "вопросов")}`;
  return b.asked > 0 ? `${items} · спрашивали ${b.asked} ${plural(b.asked, "раз", "раза", "раз")}` : items;
}

/** Импорт CSV в банк (A1): тело — текст файла, ответ — отчёт. */
export function useImportQuestions(bank: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (csv: string) => {
      const { data, error, response } = await api.POST("/admin/questions/banks/{bank}/import", {
        params: { path: { bank } },
        body: csv,
        bodySerializer: (b) => b,
        headers: { "Content-Type": "text/csv" },
      });
      if (!data) {
        const e = error as { message?: string } | undefined;
        throw new Error(e?.message ?? `Импорт не удался (${response.status}).`);
      }
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["questions"] }),
  });
}

/** Мок-интервью (SP4 §4, M3): рубрики, записи студента, новая запись. */
export type Rubric = components["schemas"]["Rubric"];
export type Interview = components["schemas"]["Interview"];

export function useRubrics() {
  return useQuery({
    queryKey: ["rubrics"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/mentor/rubrics");
      if (!data) throw new Error((error as { message?: string } | undefined)?.message ?? `Рубрики не загрузились (${response.status}).`);
      return data;
    },
    staleTime: 10 * 60_000,
  });
}

export function useInterviews(userId: string) {
  return useQuery({
    queryKey: ["interviews", userId],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/mentor/students/{userId}/interviews", { params: { path: { userId } } });
      if (!data) throw new Error((error as { message?: string } | undefined)?.message ?? `Интервью не загрузились (${response.status}).`);
      return data;
    },
  });
}

export function useRecordInterview(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { rubric_id: string; scores: Record<string, number>; notes?: string; held_on?: string }) => {
      const { data, error, response } = await api.POST("/mentor/students/{userId}/interviews", { params: { path: { userId } }, body });
      if (!data) throw new Error((error as { message?: string } | undefined)?.message ?? `Не удалось записать интервью (${response.status}).`);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["interviews", userId] }),
  });
}
